import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { Goal } from '@/types/financial';
import { useAuth } from '../AuthContext';
import { useProfile } from '../ProfileContext';
import { useEncryption } from '../EncryptionContext';
import { loadGoalsFromSupabase } from '@/lib/supabase-operations';
import { goalsReducer, initialState } from './reducer';
import { useGoalSelectors } from './selectors';
import { createGoal, updateGoal as updateGoalOp, deleteGoal as deleteGoalOp } from './operations';
import { logger } from '@/utils/logger';

interface GoalsContextType {
  goals: Goal[];
  addGoal: (goal: Omit<Goal, 'id' | 'createdAt'>) => Promise<Goal>;
  updateGoal: (id: string, updates: Partial<Goal>) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  getGoalById: (id: string) => Goal | undefined;
  isLoading: boolean;
}

const GoalsContext = createContext<GoalsContextType | undefined>(undefined);

export const GoalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(goalsReducer, initialState);
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { isEncryptionReady, isCheckingEncryption } = useEncryption();
  
  const selectors = useGoalSelectors(state.goals);

  // Load goals from Supabase
  useEffect(() => {
    if (!user) {
      dispatch({ type: 'CLEAR_GOALS' });
      return;
    }

    if (isCheckingEncryption) {
      logger.debug('[Goals] Waiting for encryption check...');
      return;
    }
    
    if (!isEncryptionReady) {
      logger.warn('[Goals] Encryption not ready, skipping load');
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    const loadGoals = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        logger.info('[Goals] Loading goals from Supabase');
        const supabaseGoals = await loadGoalsFromSupabase();
        const mappedGoals = supabaseGoals.map(goal => ({
          ...goal,
          currentValue: goal.currentValue || goal.saved || 0,
          targetValue: goal.targetValue || goal.amount || 0
        }));
        dispatch({ type: 'SET_GOALS', payload: mappedGoals });
        logger.info('[Goals] Loaded goals', { count: supabaseGoals.length });
      } catch (error) {
        logger.error('[Goals] Error loading goals', { error });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadGoals();
  }, [user, isEncryptionReady, isCheckingEncryption]);

  // Reload when profile changes
  useEffect(() => {
    if (user && activeProfile && isEncryptionReady && !isCheckingEncryption) {
      logger.info('[Goals] Profile changed, reloading goals', { profileId: activeProfile.id });
      
      const loadGoals = async () => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
          const supabaseGoals = await loadGoalsFromSupabase();
          const mappedGoals = supabaseGoals.map(goal => ({
            ...goal,
            currentValue: goal.currentValue || goal.saved || 0,
            targetValue: goal.targetValue || goal.amount || 0
          }));
          dispatch({ type: 'SET_GOALS', payload: mappedGoals });
        } catch (error) {
          logger.error('[Goals] Error reloading goals', { error });
        } finally {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      };
      
      loadGoals();
    }
  }, [activeProfile?.id, user, isEncryptionReady, isCheckingEncryption]);

  const addGoal = useCallback(async (
    goalData: Omit<Goal, 'id' | 'createdAt'>
  ): Promise<Goal> => {
    try {
      const newGoal = await createGoal(goalData);
      dispatch({ type: 'ADD_GOAL', payload: newGoal });
      return newGoal;
    } catch (error) {
      logger.error('[Goals] Error adding goal', { error });
      throw error;
    }
  }, []);

  const updateGoal = useCallback(async (id: string, updates: Partial<Goal>) => {
    try {
      const currentGoal = state.goals.find(g => g.id === id);
      if (!currentGoal) throw new Error('Goal not found');

      const updatedGoal = await updateGoalOp(id, updates, currentGoal);
      dispatch({ type: 'UPDATE_GOAL', payload: { id, updates: updatedGoal } });
    } catch (error) {
      logger.error('[Goals] Error updating goal', { error });
      throw error;
    }
  }, [state.goals]);

  const deleteGoal = useCallback(async (id: string) => {
    try {
      await deleteGoalOp(id);
      dispatch({ type: 'DELETE_GOAL', payload: id });
    } catch (error) {
      logger.error('[Goals] Error deleting goal', { error });
      throw error;
    }
  }, []);

  const value = useMemo<GoalsContextType>(() => ({
    goals: state.goals,
    addGoal,
    updateGoal,
    deleteGoal,
    getGoalById: selectors.getById,
    isLoading: state.isLoading,
  }), [state, addGoal, updateGoal, deleteGoal, selectors]);

  return (
    <GoalsContext.Provider value={value}>
      {children}
    </GoalsContext.Provider>
  );
};

export const useGoals = () => {
  const context = useContext(GoalsContext);
  if (!context) {
    throw new Error('useGoals must be used within a GoalsProvider');
  }
  return context;
};
